import { traceCrash } from '../debug/crashTrace';

const AUTH_REDIRECT_STATE_KEY = 'epos:auth-redirect';
const AUTH_REDIRECT_WINDOW_MS = 3_000;

interface AuthRedirectState {
  at: number;
  from: string;
  reason: string;
}

const PUBLIC_AUTH_PATHS = ['/login', '/forgotPassword', '/reset-password'];

const getCurrentUrl = (): string =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

export const isPublicAuthRoute = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return PUBLIC_AUTH_PATHS.some((path) => window.location.pathname.startsWith(path));
};

const readRedirectState = (): AuthRedirectState | null => {
  try {
    const rawValue = window.sessionStorage.getItem(AUTH_REDIRECT_STATE_KEY);
    if (!rawValue) return null;

    return JSON.parse(rawValue) as AuthRedirectState;
  } catch (error) {
    console.warn('[EPOS] No se pudo leer el estado de redirección por sesión.', error);
    return null;
  }
};

const writeRedirectState = (state: AuthRedirectState): void => {
  try {
    window.sessionStorage.setItem(AUTH_REDIRECT_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('[EPOS] No se pudo guardar el estado de redirección por sesión.', error);
  }
};

export const isAuthRedirectInProgress = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const previousState = readRedirectState();
  return Boolean(previousState && previousState.at + AUTH_REDIRECT_WINDOW_MS > Date.now());
};

export const redirectToLoginAfterUnauthorized = (reason: string): boolean => {
  if (typeof window === 'undefined' || isPublicAuthRoute()) {
    return false;
  }

  const from = getCurrentUrl();
  const previousState = readRedirectState();
  const now = Date.now();

  if (
    previousState &&
    previousState.from === from &&
    previousState.at + AUTH_REDIRECT_WINDOW_MS > now
  ) {
    return false;
  }

  writeRedirectState({ at: now, from, reason });

  const redirectParam = from && from !== '/' ? `?redirect=${encodeURIComponent(from)}` : '';
  const loginUrl = `/login${redirectParam}`;

  traceCrash('auth.unauthorized.redirect_to_login', {
    reason,
    from,
    loginUrl,
  });

  window.location.replace(loginUrl);
  return true;
};
